import { FC } from "react";
import { useTranslation } from "react-i18next";

import { Button, Buttons } from "capitalrx-components-2.0/lib/general";
import { Grid, Shelf, Stack, useResponsive } from "capitalrx-components-2.0/lib/layout";

import { Textbox } from "@/App/components";
import { StackOrShelf } from "@/App/components/StackOrShelf";
import { validateDecimal, validateNumerical } from "@/App/utils/validation";


interface DirectMemberReimbursementCompoundPrescriptionIngredientFieldProps {
  readonly isRemovable?: boolean;
  readonly index: number;
  readonly removeIngredientCallback: (index: number | number[]) => void;
}

const TRANSLATION_PATH = "DIRECT_MEMBER_REIMBURSEMENT.directMemberReimbursementCompoundPrescriptionForm";

export const DirectMemberReimbursementCompoundPrescriptionIngredientField: FC<DirectMemberReimbursementCompoundPrescriptionIngredientFieldProps> = ({
  isRemovable,
  index,
  removeIngredientCallback,
}) => {
  const { t: translate } = useTranslation();
  const { fieldColumns, isTwoColumnsDivided, extraSpacing } = useResponsive({
    fieldColumns: {
      desktop: 4,
      tablet: 4,
      mobile: 12,
    },
    isTwoColumnsDivided: {
      desktop: true,
      tablet: false,
      mobile: false,
    },
    extraSpacing: {
      desktop: false,
      tablet: false,
      mobile: true,
    },
  });
  return (
    <Stack spacing="medium">
      <Grid>
        <Grid.Span columns={isTwoColumnsDivided ? 6 : 12}>
          <Grid>
            <Grid.Span columns={fieldColumns}>
              <Textbox.Field
                name={`ingredients[${index}].ndc`}
                label={translate(`${TRANSLATION_PATH}.11DigitIngredientNDC`)}
                minLength={11}
                maxLength={11}
                validator={(value) => validateNumerical(value, translate)}
                useDigitsCounter
                required
              />
            </Grid.Span>
            <Grid.Span columns={fieldColumns}>
              <Textbox.Field
                name={`ingredients[${index}].quantity`}
                label={translate(`${TRANSLATION_PATH}.quantity`)}
                required
                validator={(value) => validateDecimal(value, translate, { maxDecimalPlaces: 2, greaterThan: 0 })}
              />
            </Grid.Span>
            <Grid.Span columns={fieldColumns}>
              <Textbox.Field
                name={`ingredients[${index}].ingredient_cost`}
                label={translate(`${TRANSLATION_PATH}.ingredientCost`)}
                prefix="$"
                instructions={translate(`${TRANSLATION_PATH}.noCommasOrSpecialCharacters`)}
                required
                validator={(value) => validateDecimal(value, translate, { maxDecimalPlaces: 2, greaterThan: 0 })}
              />
            </Grid.Span>
          </Grid>
        </Grid.Span>
        {isRemovable && (
          <Grid.Span columns={isTwoColumnsDivided ? 6 : 12}>
            <StackOrShelf alignment="center" style={{ height: "100%" }}>
              <Button
                appearance={Buttons.Destructive.Tertiary.Small}
                onClick={() => removeIngredientCallback(index)}
              >
                {translate(`${TRANSLATION_PATH}.remove`)}
              </Button>
              <Shelf.Spacer />
            </StackOrShelf>
          </Grid.Span>
        )}
      </Grid>
      {extraSpacing && <div></div>}
    </Stack>
  );
};
